import "../../../chunk-D57E6H3M.mjs";

// src/components/primitive/TextField/TextField.tsx
import * as React from "react";
import {
  MdEmail,
  MdLock,
  MdPerson,
  MdSearch,
  MdVisibility,
  MdVisibilityOff
} from "react-icons/md";
import { useComponentsTranslation } from "../../../i18n/useComponentsTranslation.mjs";
import { textFieldVariants } from "./textFieldVariants.mjs";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var ICONS = {
  "eye": MdVisibility,
  "eye-off": MdVisibilityOff,
  "mail": MdEmail,
  "lock": MdLock,
  "search": MdSearch,
  "person": MdPerson
};
function renderIcon(name) {
  if (!name) return null;
  const IconComp = ICONS[name];
  return IconComp ? /* @__PURE__ */ jsx(IconComp, { "aria-hidden": true }) : null;
}
function TextField({ ref, ...props }) {
  const {
    label,
    helperText,
    error: errorProp = false,
    errorText,
    size = "md",
    variant = "outlined",
    fullWidth = false,
    multiline = false,
    rows = 4,
    showPasswordToggle,
    reserveHelperSpace = false,
    startIcon,
    accessory,
    accessoryLayout = "attached",
    type = "text",
    disabled,
    id: idProp,
    className = "",
    "aria-describedby": ariaDescribedByProp,
    min,
    max,
    step,
    ...rest
  } = props;
  const { t } = useComponentsTranslation();
  const reactId = React.useId();
  const id = idProp ?? `buyscout-tf-${reactId}`;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const error = errorProp || Boolean(errorText);
  const isPasswordField = type === "password";
  const toggleEnabled = (showPasswordToggle ?? isPasswordField) && !multiline;
  const [showPassword, setShowPassword] = React.useState(false);
  const effectiveType = isPasswordField && showPassword ? "text" : type;
  const rootClasses = [
    textFieldVariants({ variant, size, accessoryLayout: accessory ? accessoryLayout : "attached" }),
    error ? "buyscout-tf--error" : "",
    disabled ? "buyscout-tf--disabled" : "",
    fullWidth ? "buyscout-tf--full-width" : "",
    multiline ? "buyscout-tf--multiline" : "",
    accessory ? "buyscout-tf--with-accessory" : "",
    reserveHelperSpace ? "buyscout-tf--reserve-helper" : "",
    className
  ].filter(Boolean).join(" ");
  const describedBy = [helperText ? helperId : "", errorText ? errorId : "", ariaDescribedByProp ?? ""].filter(Boolean).join(" ") || void 0;
  const inputContent = /* @__PURE__ */ jsxs(Fragment, { children: [
    startIcon && /* @__PURE__ */ jsx("span", { className: "buyscout-tf__adornment buyscout-tf__adornment--start", children: renderIcon(startIcon) }),
    multiline ? /* @__PURE__ */ jsx(
      "textarea",
      {
        ...rest,
        id,
        ref,
        rows,
        disabled,
        "aria-invalid": error || void 0,
        "aria-describedby": describedBy,
        className: "buyscout-tf__input"
      }
    ) : /* @__PURE__ */ jsx(
      "input",
      {
        ...rest,
        id,
        ref,
        type: effectiveType,
        min,
        max,
        step,
        disabled,
        "aria-invalid": error || void 0,
        "aria-describedby": describedBy,
        className: "buyscout-tf__input"
      }
    ),
    toggleEnabled ? /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "aria-label": t("textField.showPassword"),
        "aria-pressed": showPassword,
        className: "buyscout-tf__adornment buyscout-tf__adornment--end buyscout-tf__toggle",
        onMouseDown: (e) => e.preventDefault(),
        onClick: () => setShowPassword((v) => !v),
        disabled,
        children: /* @__PURE__ */ jsxs("span", { className: "buyscout-tf__toggle-glyphs", "aria-hidden": true, children: [
          /* @__PURE__ */ jsx(MdVisibility, { "data-shown": !showPassword || void 0 }),
          /* @__PURE__ */ jsx(MdVisibilityOff, { "data-shown": showPassword || void 0 })
        ] })
      }
    ) : null
  ] });
  return /* @__PURE__ */ jsxs("div", { className: rootClasses, children: [
    label && /* @__PURE__ */ jsx("label", { htmlFor: id, className: "buyscout-tf__label", children: label }),
    /* @__PURE__ */ jsx("div", { className: "buyscout-tf__chrome", children: /* @__PURE__ */ jsxs("div", { className: "buyscout-tf__field", children: [
      accessory && accessoryLayout === "responsive-toolbar" ? /* @__PURE__ */ jsx("div", { className: "buyscout-tf__accessory", children: accessory }) : null,
      accessory ? /* @__PURE__ */ jsx("div", { className: `buyscout-tf__editor${accessoryLayout === "responsive-toolbar" ? " buyscout-tf__toolbar-editor" : ""}`, children: inputContent }) : inputContent,
      accessory && accessoryLayout !== "responsive-toolbar" ? /* @__PURE__ */ jsx("div", { className: "buyscout-tf__accessory", children: accessory }) : null
    ] }) }),
    (helperText || reserveHelperSpace && !errorText) && /* @__PURE__ */ jsx(
      "span",
      {
        id: helperText ? helperId : void 0,
        className: `buyscout-tf__helper${error && !errorText ? " buyscout-tf__helper--error" : ""}`,
        children: helperText
      }
    ),
    errorText && /* @__PURE__ */ jsx("span", { id: errorId, className: "buyscout-tf__helper buyscout-tf__helper--error", children: errorText })
  ] });
}
TextField.displayName = "TextField";
export {
  TextField
};
//# sourceMappingURL=TextField.mjs.map